/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useRetracked } from 'js/lib/retracked';
import { SHORT_MONTH_DAY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, Dialog, Radio, RadioGroup, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type { CommonSpecialization } from 'bundles/enroll-common/utils/mixAndMatchUtils';
import { getS12nOrder } from 'bundles/enroll-common/utils/mixAndMatchUtils';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import paymentsExperiments from 'bundles/epic/clients/__generated__/payments';
import { getS12nDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/enroll';

type Props = {
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
  onSubmit: (selectedS12nId: string) => void;
  isTerminal: boolean;
  isLoading?: boolean;
};

const styles = {
  dialogSupportText: css`
    margin-bottom: var(--cds-spacing-200);
  `,
};

const CourseEnrollS12nSelectionModalV2 = ({ onClose, onSubmit, isTerminal, isLoading = false }: Props) => {
  const { course, s12ns, s12nCourses, enrollmentAvailableChoices, partners } = useCourseEnrollModalData();
  const initialS12nId = s12ns ? getS12nOrder(course.id, s12ns)[0]?.id : undefined;
  const [selectedS12nId, setSelectedS12nId] = useState<string | undefined>(initialS12nId);

  const retrackedTrack = useRetracked();
  const track = useTracker();
  const eventingV3ProductData = {
    product: {
      id: course.id,
      name: course.name,
      slug: course.slug,
      type: 'course' as const,
    },
  };

  useEffect(() => {
    track('view_modal', {
      modal: {
        name: 'course_enroll_learning_program_selection',
      },
      ...eventingV3ProductData,
    });

    retrackedTrack({
      trackingName: 'course_enroll_s12n_selection_modal',
      action: 'show',
      trackingData: {
        courseId: course.id,
      },
    });

    if (s12ns && s12nCourses && s12ns.length !== enrollmentAvailableChoices.enrollmentS12nIds.length) {
      const foundS12nIds = s12ns.map((s12n) => s12n.id);
      const missingS12nIds = enrollmentAvailableChoices.enrollmentS12nIds.filter(
        (s12nId) => !foundS12nIds.includes(s12nId)
      );
      retrackedTrack({
        trackingData: {
          courseId: course.id,
          missingS12nIds,
        },
        trackingName: 'course_enroll_s12n_selection_modal_s12ns_mismatch',
        action: 'view',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!s12ns || !s12nCourses || !selectedS12nId) {
    return null;
  }

  const selectedS12n = s12ns.find(({ id }) => id === selectedS12nId);
  const orderedS12ns = getS12nOrder(course.id, s12ns).filter((s12n) => !!s12n?.id);
  const isPreenrollmentEnabled = !paymentsExperiments.preview('disablePreEnrollment');

  const getButtonText = (): string => {
    if (isPreenrollmentEnabled && enrollmentAvailableChoices.preEnrolledS12nIds.includes(selectedS12nId)) {
      return _t('You’re already pre-enrolled');
    }

    return isTerminal ? _t('Continue') : _t('Next');
  };

  const getCoursePositionText = (s12n: CommonSpecialization, courseId: string) => {
    const coursePositionInS12n = 'courseIds' in s12n && s12n?.courseIds?.indexOf(courseId) + 1;
    return (
      coursePositionInS12n &&
      _t('(Course #{coursePosition} of #{courseCount})', {
        coursePosition: coursePositionInS12n,
        courseCount: s12n?.courseIds.length,
      })
    );
  };

  const getPreEnrollmentText = (s12nId: string, plannedLaunchDate: Date) => {
    const shouldShowPreenrollmentStatus = enrollmentAvailableChoices.canPreEnrollOrIsPreEnrolledS12n(s12nId);
    const isPreEnrolled = enrollmentAvailableChoices.preEnrolledS12nIds.includes(s12nId);
    return (
      shouldShowPreenrollmentStatus &&
      _t(' (#{preenrollmentStatus} — opens #{startDate})', {
        startDate: formatDateTimeDisplay(plannedLaunchDate, SHORT_MONTH_DAY_DISPLAY),
        preenrollmentStatus: isPreEnrolled ? _t('Pre-enrolled') : _t('Pre-enrollment available'),
      })
    );
  };

  const handleSubmit = () => {
    if (selectedS12n) {
      track('click_course_specialization_selection_button', {
        ...eventingV3ProductData,
        specialization: {
          id: selectedS12n.id,
          name: selectedS12n.name,
          slug: selectedS12n.slug,
        },
        specializationOrderNumber: orderedS12ns.map(({ id }) => id).indexOf(selectedS12n.id) + 1, // 1-indexed
        specializationCount: orderedS12ns.length,
      });
    }

    retrackedTrack({
      trackingName: 'course_enroll_s12n_selection_button',
      action: 'click',
      trackingData: {
        courseId: course.id,
        s12nId: selectedS12nId,
      },
    });
    onSubmit(selectedS12nId);
  };

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <Dialog open onClose={() => onClose()} sticky="actions">
      <HeadingGroup>{_t('Choose a program')}</HeadingGroup>
      <Content>
        <Typography2 component="p" css={styles.dialogSupportText}>
          <FormattedMessage
            message={_t(
              // eslint-disable-next-line no-restricted-syntax
              'This course, {courseAndPartner}, is part of multiple programs. Please select the program you would like to enroll in.'
            )}
            courseAndPartner={
              <strong>
                {_t('#{courseName} by #{partner}', {
                  courseName: course.name,
                  partner: partners?.map(({ name }) => name).join(', '),
                })}
              </strong>
            }
          />
        </Typography2>

        <RadioGroup
          value={selectedS12nId}
          onChange={(e) => setSelectedS12nId(e.target.value)}
          aria-label={_t('Choose a program')}
        >
          {orderedS12ns.map((s12n) => {
            const plannedLaunchDate = enrollmentAvailableChoices.getPlannedLaunchDateForS12n(s12n.id);
            const supportText =
              isPreenrollmentEnabled && plannedLaunchDate && getPreEnrollmentText(s12n.id, plannedLaunchDate);
            const coursePositionText = getCoursePositionText(s12n, course.id) || '';

            return (
              <Radio
                key={s12n.id}
                value={s12n.id}
                label={`${getS12nDisplayName(s12n.name, s12n.productVariant)} ${coursePositionText}`}
                supportText={supportText}
              />
            );
          })}
        </RadioGroup>
      </Content>
      <Actions>
        <Button
          data-e2e="course_enroll_s12n_selection_button"
          disabled={isPreenrollmentEnabled && enrollmentAvailableChoices.preEnrolledS12nIds.includes(selectedS12nId)}
          onClick={handleSubmit}
          loading={isLoading}
        >
          {getButtonText()}
        </Button>
      </Actions>
    </Dialog>
  );
};

export default CourseEnrollS12nSelectionModalV2;
